// TechnicianPresentationsTab.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Alert, Badge, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import Hoot from "../../Hoot";

const normalize = (str) => (str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() : "");
const safeValue = (v, fallback = "—") => (v === null || v === undefined || v === "" ? fallback : v);

const fmtDateInput = (d) => {
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const addDays = (d, days) => {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
};

const statusOptions = [
  { label: "Parado", value: "parado" },
  { label: "Contactado", value: "contacto" },
  { label: "Em andamento / Apresentado", value: "em andamento" },
  { label: "Em uso", value: "em uso" },
  { label: "Rejeitado", value: "rejeitado" },
  { label: "Não atende", value: "não atende" },
  { label: "Interessado", value: "interessado" },
];

const ui = `


.wrap{
     display:flex;
    flex-wrap:wrap;
}

.pres-tab .toolbar{
  padding:10px 10px;
  border-radius:14px;
  background:linear-gradient(180deg, rgba(148,163,184,0.12), rgba(148,163,184,0.06));
  border:1px solid rgba(226,232,240,.7);
  margin-bottom:10px;
}
.pres-tab .grid{
  display:grid;
  grid-template-columns: 1.15fr .85fr;
  gap:12px;
}
@media (max-width: 980px){
  .pres-tab .grid{ grid-template-columns: 1fr; }
}
.pres-tab .cardlet{
  background: rgba(255,255,255,.95);
  border: 1px solid rgba(226,232,240,.95);
  border-radius: 16px;
  padding: 10px 12px;
}
.pres-tab .cardlet h6{
  margin:0 0 8px;
  font-weight:750;
  color:#0f172a;
  font-size:.92rem;
  display:flex;
  gap:8px;
  align-items:center;
}
.pres-tab .table-smx{
  width:100%;
  border-collapse:collapse;
  font-size:.84rem;
}
.pres-tab .table-smx th{
  text-align:left;
  font-size:.75rem;
  color:#64748b;
  font-weight:700;
  border-bottom:1px solid rgba(226,232,240,.95);
  padding:8px 8px;
}
.pres-tab .table-smx td{
  border-bottom:1px solid rgba(226,232,240,.95);
  padding:8px 8px;
  vertical-align:middle;
}
.pres-tab .pill{
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:4px 10px;
  border-radius:999px;
  border:1px solid rgba(15,23,42,.08);
  background:rgba(226,232,240,.55);
  font-size:.74rem;
  font-weight:700;
  color:#0f172a;
}
.pres-tab .muted{ color:#64748b; }
.pres-tab .note-row{
  display:grid;
  grid-template-columns: 140px 1fr 110px;
  gap:8px;
  align-items:start;
}
@media (max-width: 640px){
  .pres-tab .note-row{ grid-template-columns: 1fr; }
}
.pres-tab textarea.form-control{ min-height: 42px; }
`;

function NotesTable({ contactId }) {
  const API_GET = (id, from, to) => Hoot() + `contacts/${id}/notes?from=${from}&to=${to}`;
  const API_POST = (id) => Hoot() + `contacts/${id}/notes`;
  const API_PUT = (id, noteId) => Hoot() + `contacts/${id}/notes/${noteId}`;

  const now = useMemo(() => new Date(), []);
  const [from, setFrom] = useState(() => fmtDateInput(addDays(now, -30)));
  const [to, setTo] = useState(() => fmtDateInput(now));

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [rows, setRows] = useState([]);
  const [savingIds, setSavingIds] = useState({});

  const abortRef = useRef(false);

  const load = async () => {
    if (!contactId) return;
    setLoading(true);
    setErr(null);

    try {
      const r = await axios.get(API_GET(contactId, from, to), { withCredentials: true });
      const data = r?.data?.data ?? [];
      if (abortRef.current) return;
      setRows(
        (Array.isArray(data) ? data : []).map((x) => ({
          id: x?.id,
          date: x?.created_at || fmtDateInput(new Date()),
          description: x?.description || "",
          isNew: false,
        }))
      );
    } catch (e) {
      if (!abortRef.current) {
        setRows([]);
        setErr(e?.response?.data?.msg || "Erro ao carregar notas.");
      }
    } finally {
      if (!abortRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    abortRef.current = false;
    load();
    return () => {
      abortRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, from, to]);

  const addRow = () => {
    setRows((p) => [
      {
        id: null,
        date: fmtDateInput(new Date()),
        description: "",
        isNew: true,
        _tmp: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      },
      ...p,
    ]);
  };

  const setRow = (idx, patch) => {
    setRows((p) => p.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const saveRow = async (idx) => {
    const row = rows[idx];
    if (!contactId || !row) return;

    const key = row.id ? String(row.id) : row._tmp || `new_${idx}`;
    if (savingIds[key]) return;

    setSavingIds((p) => ({ ...p, [key]: true }));
    setErr(null);

    try {
      if (!row.id) {
        const r = await axios.post(
          API_POST(contactId),
          { description: row.description || "", date: row.date },
          { withCredentials: true }
        );
        const created = r?.data?.data;
        const newId = created?.id;
        setRows((p) =>
          p.map((x, i) =>
            i === idx
              ? {
                  ...x,
                  id: newId || x.id,
                  isNew: false,
                  date: created?.created_at || x.date,
                  description: created?.description ?? x.description,
                }
              : x
          )
        );
      } else {
        const r = await axios.put(
          API_PUT(contactId, row.id),
          { description: row.description || "", date: row.date },
          { withCredentials: true }
        );
        const updated = r?.data?.data;
        setRows((p) =>
          p.map((x, i) =>
            i === idx
              ? {
                  ...x,
                  date: updated?.created_at || x.date,
                  description: updated?.description ?? x.description,
                  isNew: false,
                }
              : x
          )
        );
      }
    } catch (e) {
      setErr(e?.response?.data?.msg || "Erro ao guardar nota.");
    } finally {
      setSavingIds((p) => {
        const next = { ...p };
        delete next[key];
        return next;
      });
    }
  };

  return (
    <div className="cardlet">
      <h6>
        <i className="bi bi-journal-text" /> Notas da apresentação{" "}
        <Badge bg="light" text="dark" style={{ borderRadius: 999, border: "1px solid rgba(226,232,240,.95)" }}>
          auto-save
        </Badge>
      </h6>

      {err ? (
        <Alert variant="danger" style={{ borderRadius: 14, marginBottom: 10 }}>
          {err}
        </Alert>
      ) : null}

      <div className="toolbar" style={{ marginBottom: 10 }}>
        <div className="d-flex gap-2 flex-wrap align-items-end justify-content-between">
          <div className="d-flex gap-2 flex-wrap align-items-end">
            <Form.Group>
              <Form.Label style={{ fontSize: ".78rem", fontWeight: 700, color: "#475569" }}>De</Form.Label>
              <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} disabled={loading} />
            </Form.Group>
            <Form.Group>
              <Form.Label style={{ fontSize: ".78rem", fontWeight: 700, color: "#475569" }}>Até</Form.Label>
              <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} disabled={loading} />
            </Form.Group>
            <Button variant="outline-dark" className="rounded-pill" disabled={loading} onClick={load}>
              <i className="bi bi-arrow-clockwise me-1" />
              Recarregar
            </Button>
          </div>

          <Button variant="primary" className="rounded-pill" onClick={addRow} disabled={!contactId}>
            <i className="bi bi-plus-lg me-1" />
            Nova nota
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="muted d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" /> a carregar…
        </div>
      ) : !rows.length ? (
        <div className="muted">Sem notas nesta janela.</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {rows.map((r, idx) => {
            const key = r.id ? String(r.id) : r._tmp || `new_${idx}`;
            const saving = !!savingIds[key];

            return (
              <div key={key} className="note-row">
                <Form.Control
                  type="date"
                  value={r.date || ""}
                  onChange={(e) => setRow(idx, { date: e.target.value })}
                  onBlur={() => saveRow(idx)}
                  disabled={!contactId || saving}
                />

                <Form.Control
                  as="textarea"
                  rows={2}
                  value={r.description || ""}
                  placeholder="Descreve como correu a apresentação…"
                  onChange={(e) => setRow(idx, { description: e.target.value })}
                  onBlur={() => saveRow(idx)}
                  disabled={!contactId || saving}
                />

                <div className="d-flex gap-2 justify-content-end">
                  <Button
                    variant="outline-primary"
                    className="rounded-pill"
                    disabled={!contactId || saving}
                    onClick={() => saveRow(idx)}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Guardando
                      </>
                    ) : r.id ? (
                      "Atualizar"
                    ) : (
                      "Guardar"
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function TechnicianPresentationsTab({
  techUserId,
  techName,
  defaultFromDays = 30,
  meetingTypeValue = "apresentacao",
  allowQuickCreateSchool = true,
}) {
  const API_CONTACTS_LIST = useMemo(() => Hoot() + "contacts/list", []);
  const API_UPDATE_STATUS = useMemo(() => Hoot() + "contacts/update-status", []);
  const API_CREATE_CONTACT = useMemo(() => Hoot() + "contacts/create", []);
  const API_USER_ACTIVITIES = (userId, from, to) => Hoot() + `users/${userId}/activities?from=${from}&to=${to}`;

  const now = useMemo(() => new Date(), []);
  const [from, setFrom] = useState(() => fmtDateInput(addDays(now, -defaultFromDays)));
  const [to, setTo] = useState(() => fmtDateInput(now));

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);

  const [search, setSearch] = useState("");
  const [selectedContactId, setSelectedContactId] = useState(null);

  const [creatingSchool, setCreatingSchool] = useState(false);
  const [createForm, setCreateForm] = useState({
    ed_contact_name: "",
    ed_contact_phone: "",
    ed_contact_email: "",
    ed_contact_country: "",
    ed_contact_address: "",
    ed_contact_type: "escola",
    ed_contact_status: "parado",
    ed_contact_comercial: "",
    ed_contact_province: "",
  });

  const abortRef = useRef(false);

  const load = async () => {
    if (!techUserId) return;
    setLoading(true);
    setErr(null);

    try {
      const [cRes, aRes] = await Promise.all([
        axios.get(API_CONTACTS_LIST, { withCredentials: true }),
        axios.get(API_USER_ACTIVITIES(techUserId, from, to), { withCredentials: true }),
      ]);

      const cRows = cRes?.data?.data ?? [];
      const aRows = aRes?.data?.data ?? [];

      if (abortRef.current) return;

      setContacts(Array.isArray(cRows) ? cRows : []);
      setActivities(Array.isArray(aRows) ? aRows : []);
    } catch (e) {
      if (!abortRef.current) {
        setContacts([]);
        setActivities([]);
        setErr(e?.response?.data?.msg || "Erro ao carregar apresentações.");
      }
    } finally {
      if (!abortRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    abortRef.current = false;
    load();
    return () => {
      abortRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [techUserId, from, to]);

  const presentations = useMemo(() => {
    const mt = normalize(meetingTypeValue);
    return (activities || []).filter((a) => normalize(a?.meeting_type || a?.meetingType || "") === mt);
  }, [activities, meetingTypeValue]);

  const presentationsByContact = useMemo(() => {
    const map = new Map();

    presentations.forEach((a) => {
      const cid = a?.meeting_contact_id || a?.meetingContactId || null;
      if (!cid) return;

      if (!map.has(cid)) {
        map.set(cid, {
          contact_id: cid,
          contact_name: a?.contact_name || null,
          contact_status: a?.contact_status || null,
          last_date: a?.meeting_date || null,
          count: 0,
          rows: [],
        });
      }

      const item = map.get(cid);
      item.count += 1;
      item.rows.push(a);

      const d = a?.meeting_date || null;
      if (d && (!item.last_date || String(d) > String(item.last_date))) item.last_date = d;

      const cs = a?.contact_status ?? null;
      if (cs) item.contact_status = cs;
      const cn = a?.contact_name ?? null;
      if (cn) item.contact_name = cn;
    });

    const arr = Array.from(map.values()).sort((x, y) => {
      if (y.count !== x.count) return y.count - x.count;
      return String(y.last_date || "").localeCompare(String(x.last_date || ""));
    });

    return arr;
  }, [presentations]);

  const contactsIndex = useMemo(() => {
    const m = new Map();
    (contacts || []).forEach((c) => {
      const id = c?.ed_contact_id ?? c?.id;
      if (!id) return;
      m.set(Number(id), c);
    });
    return m;
  }, [contacts]);

  const mergedSchools = useMemo(() => {
    const sNorm = normalize(search);
    return presentationsByContact
      .map((p) => {
        const c = contactsIndex.get(Number(p.contact_id)) || null;
        const name = c?.ed_contact_name || p.contact_name || "";
        const status = c?.ed_contact_status || p.contact_status || "";
        return {
          contact_id: Number(p.contact_id),
          name,
          status,
          province: c?.ed_contact_province || "",
          address: c?.ed_contact_address || "",
          phone: c?.ed_contact_phone || "",
          email: c?.ed_contact_email || "",
          count: p.count,
          last_date: p.last_date,
        };
      })
      .filter((x) => {
        if (!sNorm) return true;
        return (
          normalize(x.name).includes(sNorm) ||
          normalize(x.status).includes(sNorm) ||
          normalize(x.province).includes(sNorm) ||
          normalize(x.address).includes(sNorm) ||
          normalize(x.phone).includes(sNorm) ||
          normalize(x.email).includes(sNorm)
        );
      });
  }, [presentationsByContact, contactsIndex, search]);

  useEffect(() => {
    if (!selectedContactId && mergedSchools.length) setSelectedContactId(mergedSchools[0].contact_id);
    if (selectedContactId && !mergedSchools.find((x) => x.contact_id === selectedContactId)) {
      setSelectedContactId(mergedSchools[0]?.contact_id || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergedSchools]);

  const selectedSchool = useMemo(
    () => mergedSchools.find((x) => x.contact_id === selectedContactId) || null,
    [mergedSchools, selectedContactId]
  );

  const updateStatus = async (contactId, newStatus) => {
    if (!contactId || !newStatus) return;
    try {
      await axios.post(
        API_UPDATE_STATUS,
        { contact_id: contactId, contact_status: newStatus },
        { withCredentials: true }
      );
      toast.success("Estado actualizado.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.msg || "Erro ao atualizar estado.");
    }
  };

  const createSchoolQuick = async () => {
    if (!allowQuickCreateSchool) return;

    const name = (createForm.ed_contact_name || "").trim();
    if (!name) return toast.error("Nome da escola é obrigatório.");

    setCreatingSchool(true);
    try {
      const fd = new FormData();
      Object.entries(createForm).forEach(([k, v]) => fd.append(k, v ?? ""));
      const r = await axios.post(API_CREATE_CONTACT, fd, { withCredentials: true });
      const ok = r?.data?.success;
      if (!ok) throw new Error(r?.data?.msg || "Falha ao criar escola");
      toast.success("Escola criada!");
      setCreateForm((p) => ({ ...p, ed_contact_name: "", ed_contact_phone: "", ed_contact_email: "", ed_contact_address: "" }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.msg || e?.message || "Erro ao criar escola.");
    } finally {
      setCreatingSchool(false);
    }
  };

  const kpis = useMemo(() => {
    const totalPres = presentations.length;
    const uniqSchools = presentationsByContact.length;
    const top = mergedSchools[0];
    return {
      totalPres,
      uniqSchools,
      topSchool: top?.name ? `${top.name} (${top.count})` : "—",
    };
  }, [presentations.length, presentationsByContact.length, mergedSchools]);

  return (
    <>
      <style>{ui}</style>

      <div className="pres-tab">
        {err ? (
          <Alert variant="danger" style={{ borderRadius: 14, marginBottom: 10 }}>
            {err}
          </Alert>
        ) : null}

        <div className="toolbar">
          <div className="wrap g-2">
            <div className="col-12 col-lg-12">
              <div style={{ fontWeight: 800, color: "#0f172a" }}>
                Apresentações{" "}
                {techName ? (
                  <span className="pill">
                    <i className="bi bi-person" /> {techName}
                  </span>
                ) : null}
              </div>
              <div className="muted" style={{ fontSize: ".82rem" }}>
                Filtra por período e atualiza o estado da escola + notas (auto-save).
              </div>
            </div>

            <div>
              <Form.Label style={{ fontSize: ".78rem", fontWeight: 700, color: "#475569" }}>De</Form.Label>
              <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} disabled={loading} />
            </div>

            <div>
              <Form.Label style={{ fontSize: ".78rem", fontWeight: 700, color: "#475569" }}>Até</Form.Label>
              <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} disabled={loading} />
            </div>

            <div>
              <Form.Label style={{ fontSize: ".78rem", fontWeight: 700, color: "#475569" }}>Pesquisar escola</Form.Label>
              <Form.Control
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="nome, estado, província, endereço…"
                disabled={loading}
              />
            </div>

            <div className="d-flex align-items-end justify-content-lg-end">
              <Button variant="outline-dark" className="rounded-pill" onClick={load} disabled={loading || !techUserId}>
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    …
                  </>
                ) : (
                  <>
                    <i className="bi bi-arrow-clockwise me-1" />
                    Recarregar
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="d-flex gap-2 flex-wrap mt-2">
            <span className="pill">
              <i className="bi bi-easel2" /> {kpis.totalPres} apresentações
            </span>
            <span className="pill">
              <i className="bi bi-buildings" /> {kpis.uniqSchools} escolas
            </span>
            <span className="pill">
              <i className="bi bi-trophy" /> Top: {kpis.topSchool}
            </span>
          </div>
        </div>

        <div className="block">
          <div className="cardlet">
            <h6>
              <i className="bi bi-buildings" /> Escolas visitadas{" "}
              <Badge bg="light" text="dark" style={{ borderRadius: 999, border: "1px solid rgba(226,232,240,.95)" }}>
                {mergedSchools.length}
              </Badge>
            </h6>

            {loading ? (
              <div className="muted d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" /> a carregar…
              </div>
            ) : !mergedSchools.length ? (
              <div className="muted">Sem apresentações neste período.</div>
            ) : (
              <div className="table-responsive">
                <table className="table-smx">
                  <thead>
                    <tr>
                      <th>Escola</th>
                      <th>Estado</th>
                      <th>Apres.</th>
                      <th>Última</th>
                      <th className="text-end">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mergedSchools.map((s) => (
                      <tr key={s.contact_id}>
                        <td style={{ fontWeight: 900 }}>
                          {safeValue(s.name, "—")}
                          <div className="muted" style={{ fontSize: ".78rem" }}>
                            {safeValue(s.province, "—")}
                          </div>
                        </td>
                        <td>
                          <span className="pill">{safeValue(s.status, "—")}</span>
                        </td>
                        <td style={{ fontWeight: 900 }}>{s.count}</td>
                        <td>{safeValue(s.last_date, "—")}</td>
                        <td className="text-end">
                          <Button
                            variant={selectedContactId === s.contact_id ? "primary" : "outline-primary"}
                            className="rounded-pill"
                            size="sm"
                            onClick={() => setSelectedContactId(s.contact_id)}
                          >
                            Ver
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {allowQuickCreateSchool ? (
              <div className="cardlet" style={{ marginTop: 10 }}>
                <h6>
                  <i className="bi bi-plus-circle" /> Criar escola rapidamente
                </h6>

                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <Form.Label style={{ fontSize: ".78rem", fontWeight: 700, color: "#475569" }}>Nome *</Form.Label>
                    <Form.Control
                      value={createForm.ed_contact_name}
                      onChange={(e) => setCreateForm((p) => ({ ...p, ed_contact_name: e.target.value }))}
                      placeholder="Ex.: Colégio XYZ"
                      disabled={creatingSchool}
                    />
                  </div>

                  <div className="col-12 col-md-3">
                    <Form.Label style={{ fontSize: ".78rem", fontWeight: 700, color: "#475569" }}>Telefone</Form.Label>
                    <Form.Control
                      value={createForm.ed_contact_phone}
                      onChange={(e) => setCreateForm((p) => ({ ...p, ed_contact_phone: e.target.value }))}
                      placeholder="+244…"
                      disabled={creatingSchool}
                    />
                  </div>

                  <div className="col-12 col-md-3">
                    <Form.Label style={{ fontSize: ".78rem", fontWeight: 700, color: "#475569" }}>Status</Form.Label>
                    <Form.Select
                      value={createForm.ed_contact_status}
                      onChange={(e) => setCreateForm((p) => ({ ...p, ed_contact_status: e.target.value }))}
                      disabled={creatingSchool}
                    >
                      {statusOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Form.Select>
                  </div>

                  <div className="col-12">
                    <Form.Label style={{ fontSize: ".78rem", fontWeight: 700, color: "#475569" }}>Endereço</Form.Label>
                    <Form.Control
                      value={createForm.ed_contact_address}
                      onChange={(e) => setCreateForm((p) => ({ ...p, ed_contact_address: e.target.value }))}
                      placeholder="Rua, bairro…"
                      disabled={creatingSchool}
                    />
                  </div>

                  <div className="col-12 d-flex justify-content-end">
                    <Button variant="primary" className="rounded-pill" onClick={createSchoolQuick} disabled={creatingSchool}>
                      {creatingSchool ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Criando…
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check2 me-1" /> Criar escola
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="d-flex flex-column gap-2">
            <div className="cardlet">
              <h6>
                <i className="bi bi-info-circle" /> Detalhes & estado
              </h6>

              {!selectedSchool ? (
                <div className="muted">Seleciona uma escola na lista.</div>
              ) : (
                <>
                  <div style={{ fontWeight: 900, fontSize: ".95rem" }}>{safeValue(selectedSchool.name, "—")}</div>
                  <div className="muted" style={{ fontSize: ".82rem" }}>
                    {safeValue(selectedSchool.address, "Sem endereço")} • {safeValue(selectedSchool.province, "—")}
                  </div>
                  <div className="muted" style={{ fontSize: ".82rem" }}>
                    {selectedSchool.phone ? (
                      <>
                        <i className="bi bi-telephone me-1" />
                        {selectedSchool.phone}
                      </>
                    ) : (
                      "Sem telefone"
                    )}{" "}
                    •{" "}
                    {selectedSchool.email ? (
                      <>
                        <i className="bi bi-envelope me-1" />
                        {selectedSchool.email}
                      </>
                    ) : (
                      "Sem email"
                    )}
                  </div>

                  <div className="mt-2">
                    <Form.Label style={{ fontSize: ".78rem", fontWeight: 700, color: "#475569" }}>Estado</Form.Label>
                    <div className="d-flex gap-2 align-items-center flex-wrap">
                      <span className="pill">{safeValue(selectedSchool.status, "—")}</span>

                      <Form.Select
                        size="sm"
                        style={{ maxWidth: 260 }}
                        value={selectedSchool.status || ""}
                        onChange={(e) => updateStatus(selectedSchool.contact_id, e.target.value)}
                        disabled={!selectedSchool?.contact_id || loading}
                      >
                        {statusOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Form.Select>

                      <span className="muted" style={{ fontSize: ".78rem" }}>
                        muda e guarda automaticamente
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <NotesTable contactId={selectedSchool?.contact_id || null} />
          </div>
        </div>
      </div>
    </>
  );
}